<script>
    import { onMount } from "svelte";
    import { get } from "svelte/store";
    import DocumentList from "./components/DocumentList.svelte";
    import Editor from "./components/Editor.svelte";
    import LogViewer from "./components/LogViewer.svelte";
    import logger from "./utils/logger";
    import { applyEditorStyles } from "./utils/editorStyles";
    import "highlight.js/styles/github.css";

    // Import stores
    import {
        documents,
        currentDocument,
        blocks,
        flashMessage,
        documentJustLoaded,
    } from "./stores/documentStore";

    // Import block store functionality
    import {
        updateBlock,
        createBlock,
        createBlockAtTop,
        deleteBlock,
        getFirstBlockId,
    } from "./stores/blockStore";

    // Import application controller
    import {
        initializeApp,
        switchToDocument,
        createNewDocument,
        deleteCurrentDocument,
    } from "./stores/appController";

    // UI state
    let showLogViewer = false;
    let blockRefs = {};
    let focusBlockId = null;

    // Handle block creation event from Editor component
    async function handleCreateBlock(event) {
        const { index, focus } = event.detail;
        const result = await createBlock(index, focus);
        if (result.shouldFocus) {
            focusBlockId = result.newBlockId;
        }
    }

    // Handle block creation at top event
    async function handleCreateBlockAtTop(event) {
        const { focus } = event.detail;
        const result = await createBlockAtTop(focus);
        if (result.shouldFocus) {
            focusBlockId = result.newBlockId;
        }
    }

    // Toggle log viewer
    function toggleLogViewer() {
        showLogViewer = !showLogViewer;
    }

    // Focus the first block in the document
    function focusFirstBlock() {
        const firstBlockId = getFirstBlockId();
        if (firstBlockId) {
            focusBlockId = firstBlockId;
        }
    }

    // Handle document switching with routing
    function handleSwitchDocument(e) {
        const documentId = e.detail;
        // Use the application controller to switch documents
        switchToDocument(documentId);
    }

    // Watch for changes to blocks array and focus first block if document just loaded
    $: if ($documentJustLoaded && $blocks.length > 0) {
        // Reset the flag in the store
        documentJustLoaded.set(false);

        // Delay the focus slightly to ensure components are fully mounted
        setTimeout(() => {
            focusFirstBlock();
        }, 50);
    }

    // Watch for changes to focusBlockId and focus the corresponding block
    $: if (focusBlockId && blockRefs[focusBlockId]) {
        // Use setTimeout to ensure DOM is fully updated
        setTimeout(() => {
            if (
                blockRefs[focusBlockId] &&
                blockRefs[focusBlockId].focusEditor
            ) {
                blockRefs[focusBlockId].focusEditor("start");
                focusBlockId = null; // Reset after focusing
            }
        }, 10);
    }

    // isHtmlContentEmpty was moved to appController.js

    onMount(async () => {
        logger.info("Application started");

        // Initialize the logger
        logger
            .setLevel("DEBUG")
            .setShowTimestamp(true)
            .setStoreLogsInLocalStorage(true);

        try {
            logger.info("Initializing application");

            // Initialize application using our controller
            const result = await initializeApp();

            // Apply editor styles
            applyEditorStyles();

            // The setupInitialBlocks is now called inside initializeApp
            // and will return the result directly from the initialization
            if (result && result.newBlockId) {
                focusBlockId = result.newBlockId;
            }

            logger.info("Application initialized successfully");
        } catch (error) {
            logger.error("Failed to initialize application", {
                error: error.message,
            });
            console.error("Failed to initialize:", error);
            flashMessage.set({
                type: "error",
                text: "Failed to initialize application",
            });
        }
    });
</script>

<main>
    <header class="site-header">
        <div class="logo">
            <a href="/">
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                    ></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                <span>re[stash]</span>
            </a>
        </div>
    </header>

    <!-- Document List -->
    <DocumentList
        documents={$documents}
        currentDocumentId={$currentDocument?.id}
        on:createDocument={() => createNewDocument()}
        on:switchDocument={handleSwitchDocument}
    />

    <!-- Editor Container -->
    <div
        id="editor-container"
        style="
        max-width: 800px;
        margin: 0 auto;
        min-height: 600px;
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0;
        "
    >
        <!-- White background container -->
        <div
            style="
            background-color: #fcf5e5;
            flex-grow: 1;
            "
        >
            {#each $blocks as blockItem, index (blockItem.id)}
                <!-- Add separator before blocks (except the first one) -->
                {#if index > 0}
                    <div class="block-separator">
                        <div class="squares-container">
                            {#each Array(7) as _, i}
                                <span class="square"></span>
                            {/each}
                        </div>
                    </div>
                {/if}

                <Editor
                    bind:this={blockRefs[blockItem.id]}
                    block={blockItem}
                    isFirstBlock={index === 0}
                    autoFocus={blockItem.id === focusBlockId}
                    on:update={(e) =>
                        updateBlock(e.detail.id, e.detail.content)}
                    on:createBlock={handleCreateBlock}
                    on:createBlockAtTop={handleCreateBlockAtTop}
                    on:deleteBlock={(e) => deleteBlock(e.detail.id)}
                />
            {/each}
        </div>
    </div>

    <!-- Flash Messages -->
    {#if $flashMessage.text}
        <p
            style="color: {$flashMessage.type === 'error'
                ? 'red'
                : 'green'}; margin-top: 20px; text-align: center;"
        >
            {$flashMessage.text}
        </p>
    {/if}
</main>

<style>
    /* Styles remain the same */
    main {
        padding: 1rem;
        max-width: 900px;
        margin: 0 auto;
    }
    :global(body) {
        background-color: #fcf5e5; /* Your color */
        margin: 0;
        padding: 0;
        min-height: 100vh;
        font-family: "Roboto Mono", monospace;
    }

    :global(html) {
        height: 100%;
    }

    .site-header {
        max-width: 800px;
        margin: 0 auto 12px auto;
        padding: 16px 0 8px 0;
        display: flex;
        justify-content: center;
    }

    .logo a {
        display: flex;
        align-items: center;
        gap: 8px;
        text-decoration: none;
        color: #6b7280; /* Subtle gray for reduced contrast */
        font-size: 16px;
        letter-spacing: 0.5px;
        font-weight: 500;
        transition: color 0.2s ease;
    }

    .logo a:hover {
        color: #374151; /* Slightly darker on hover */
    }

    .logo span {
        font-family: "Roboto Mono", monospace;
    }

    .block-separator {
        display: flex;
        justify-content: center;
        padding: 8px 0;
    }

    .squares-container {
        display: flex;
        gap: 10px;
    }

    .square {
        width: 4px;
        height: 4px;
        background-color: #d1d5db;
        opacity: 0.7;
        transform: rotate(45deg); /* Makes diamond shape */
    }
</style>
